import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useRouter } from 'next/router';
import Link from 'next/link';

const UrlPagination = ({ nPages, currentPage }) => {
  const router = useRouter();

  // Real <a href> links so crawlers can follow every page. Page 1 drops the
  // param so it matches the canonical URL.
  const pageHref = (pageNumber) => {
    const [pathname, search = ""] = router.asPath.split("#")[0].split("?");
    const params = new URLSearchParams(search);
    if (pageNumber > 1) params.set("page", pageNumber);
    else params.delete("page");
    const qs = params.toString();
    return qs ? `${pathname}?${qs}` : pathname;
  };

  const generatePageNumbers = (selectedPage, totalPages) => {
    if (totalPages <= 1) return totalPages === 1 ? [1] : [];

    const pages = [];
    const maxVisible = 5; // Show up to 5 page numbers

    if (totalPages <= maxVisible) {
      // Show all pages if total is small
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Smart pagination logic
      if (selectedPage <= 3) {
        pages.push(1, 2, 3, 4, '...', totalPages);
      } else if (selectedPage >= totalPages - 2) {
        pages.push(1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push(1, '...', selectedPage - 1, selectedPage, selectedPage + 1, '...', totalPages);
      }
    }

    return pages;
  };

  const pageNumbers = generatePageNumbers(currentPage, nPages);

  if (nPages <= 1) return null;

  const arrowClass = (disabled) => `
    flex items-center justify-center w-10 h-10 rounded-lg border transition-all duration-200
    ${disabled
      ? 'border-gray-200 text-gray-400 cursor-not-allowed bg-gray-50'
      : 'border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400 active:bg-gray-100'
    }
  `;

  const isFirst = currentPage === 1;
  const isLast = currentPage === nPages;

  return (
    <nav className="flex items-center justify-center" aria-label="Pagination">
      <div className="flex items-center space-x-1">
        {/* Previous Button */}
        {isFirst ? (
          <span className={arrowClass(true)} aria-label="Previous page" aria-disabled="true">
            <ChevronLeft size={16} />
          </span>
        ) : (
          <Link href={pageHref(currentPage - 1)} rel="prev" className={arrowClass(false)} aria-label="Previous page">
            <ChevronLeft size={16} />
          </Link>
        )}

        {/* Page Numbers */}
        <div className="flex items-center space-x-1">
          {pageNumbers.map((pageNumber, index) => {
            if (pageNumber === '...') {
              return (
                <span
                  key={`ellipsis-${index}`}
                  className="flex items-center justify-center w-10 h-10 text-gray-500"
                >
                  ...
                </span>
              );
            }

            const isActive = currentPage === pageNumber;

            return (
              <Link
                key={pageNumber}
                href={pageHref(pageNumber)}
                className={`
                  flex items-center justify-center w-10 h-10 rounded-lg border text-sm font-medium transition-all duration-200
                  ${isActive
                  ? 'bg-blue-600 border-blue-600 text-white shadow-sm'
                  : 'border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400 active:bg-gray-100'
                }
                `}
                aria-label={`Go to page ${pageNumber}`}
                aria-current={isActive ? 'page' : undefined}
              >
                {pageNumber}
              </Link>
            );
          })}
        </div>

        {/* Next Button */}
        {isLast ? (
          <span className={arrowClass(true)} aria-label="Next page" aria-disabled="true">
            <ChevronRight size={16} />
          </span>
        ) : (
          <Link href={pageHref(currentPage + 1)} rel="next" className={arrowClass(false)} aria-label="Next page">
            <ChevronRight size={16} />
          </Link>
        )}
      </div>
    </nav>
  );
};

export default UrlPagination;
